// ForgetPasswordForm.jsx
import React, { useState } from 'react';
import axios from 'axios';
import MessageBox from './MessageBox';

const ForgetPasswordForm = ({ onToggle }) => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [step, setStep] = useState(1); // Step 1: Email, Step 2: Verification, Step 3: New Password
  const [isLoading, setIsLoading] = useState(false);

  const requestVerificationCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post('https://app.akiya2.com/request-password-reset', { email });
      setMessage('Verification code sent to your email.');
      setMessageType('success');
      setStep(2);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Email not found. Please check your email address.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post('https://app.akiya2.com/verify-reset-code', { email, code: verificationCode });
      setMessage('Code verified successfully. Set your new password.');
      setMessageType('success');
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Invalid verification code. Please try again.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post('https://app.akiya2.com/reset-password', { 
        email, 
        code: verificationCode,
        newPassword 
      });
      setMessage('Password has been reset successfully. You can now login with your new password.');
      setMessageType('success');
      setEmail('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        onToggle(); // Redirect to login after 3 seconds
      }, 3000);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={requestVerificationCode}>
            <div className="mb-4">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border border-gray-400 p-2 w-full focus:outline-[#10c9c0]"
                required
              />
            </div>
            <button
              type="submit"
              className="bg-[#10c9c0] text-white py-2 px-4 rounded hover:bg-[#0eb1a9]"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Request Reset Code'}
            </button>
          </form>
        );
      case 2:
        return (
          <form onSubmit={verifyCode}>
            <div className="mb-4">
              <p className="text-sm mb-2">A verification code has been sent to {email}</p>
              <input
                type="text"
                placeholder="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="border border-gray-400 p-2 w-full focus:outline-[#10c9c0]"
                required
              />
            </div>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400"
                disabled={isLoading}
              >
                Back
              </button>
              <button
                type="submit"
                className="bg-[#10c9c0] text-white py-2 px-4 rounded hover:bg-[#0eb1a9]"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={requestVerificationCode}
                className="text-[#10c9c0] hover:underline text-sm"
                disabled={isLoading}
              >
                Resend Code
              </button>
            </div>
          </form>
        );
      case 3:
        return (
          <form onSubmit={resetPassword}>
            <div className="mb-4">
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="border border-gray-400 p-2 w-full focus:outline-[#10c9c0]"
                required
              />
            </div>
            <div className="mb-4">
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="border border-gray-400 p-2 w-full focus:outline-[#10c9c0]"
                required
              />
            </div>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400"
                disabled={isLoading}
              >
                Back
              </button>
              <button
                type="submit"
                className="bg-[#10c9c0] text-white py-2 px-4 rounded hover:bg-[#0eb1a9]"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Reset Password</h2>
      {message && <MessageBox message={message} type={messageType} />}
      {renderStep()}
      <p className="mt-4">
        Remembered your password?{' '}
        <button onClick={onToggle} className="text-[#10c9c0] hover:underline">
          Login
        </button>
      </p>
    </div>
  );
};

export default ForgetPasswordForm;